import { useMemo, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogTrigger,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";

import {
  AdminJobCandidateDetails,
  AdminJobDetails,
} from "@/queries/admin/job-details";
import { useAdminCandidatePipelineQuery } from "@/queries/admin/candidate-pipeline";
import { useAdminApprovedCandidatesQuery } from "@/queries/admin/approved-candidates";

import CandidateStatusForm from "@/components/forms/admin/job-candidate";

const AddCandidateDialog = ({
  jobOpening,
  candidate,
}: {
  jobOpening: AdminJobDetails;
  candidate?: AdminJobCandidateDetails;
}) => {
  const [open, setOpen] = useState(false);

  const { adminCandidatePipeline, adminCandidatePipelineLoading } =
    useAdminCandidatePipelineQuery();
  const { adminApprovedCandidates, adminApprovedCandidatesLoading } =
    useAdminApprovedCandidatesQuery();

  const currentCandidateIdsMap = useMemo(() => {
    const map = new Map<string, boolean>();
    jobOpening.candidates?.forEach((candidate) => {
      map.set(candidate.candidateId, true);
    });

    return map;
  }, [jobOpening.candidates]);

  const candidates = useMemo(() => {
    if (adminCandidatePipelineLoading || adminApprovedCandidatesLoading) {
      return [];
    }

    const candidates = [
      ...(adminApprovedCandidates?.map((candidate) => ({
        id: candidate.id,
        name: candidate.name,
        disabled: currentCandidateIdsMap.get(candidate.id),
      })) ?? []),
      ...(adminCandidatePipeline?.map((candidate) => ({
        id: candidate.id,
        name: candidate.name,
        disabled: currentCandidateIdsMap.get(candidate.id),
      })) ?? []),
    ]
      .filter(
        (
          candidate,
        ): candidate is {
          id: string;
          name: string;
          disabled: boolean | undefined;
        } => Boolean(candidate.name),
      )
      .sort((a, b) => a.name.localeCompare(b.name));

    return candidates;
  }, [
    adminCandidatePipeline,
    adminApprovedCandidates,
    adminApprovedCandidatesLoading,
    adminCandidatePipelineLoading,
    currentCandidateIdsMap,
  ]);

  const mode = useMemo(() => (candidate ? "update" : "create"), [candidate]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {mode === "update" ? (
          <Button
            variant="link"
            className="text-xs text-muted-foreground hover:underline"
          >
            View & edit details
          </Button>
        ) : (
          <Button>Add A Candidate</Button>
        )}
      </DialogTrigger>
      <DialogContent className="w-full sm:max-w-2xl">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <DialogTitle>
              {mode === "create" ? "Add a Candidate" : "Update a Candidate"}
            </DialogTitle>
          </div>
          <DialogDescription className="sr-only">
            {mode === "create" ? "Add a candidate" : "Update a candidate"}
          </DialogDescription>
        </DialogHeader>
        <ScrollArea className="sm:max-h-[calc(100vh-10rem)] md:max-h-[70vh]">
          <CandidateStatusForm
            jobOpeningId={jobOpening.id}
            candidateOptions={candidates}
            matchCriteria={[
              jobOpening.matchCriteria1 ?? "",
              jobOpening.matchCriteria2 ?? "",
              jobOpening.matchCriteria3 ?? "",
            ]}
            candidate={candidate}
            onSuccess={() => setOpen(false)}
          />
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

export default AddCandidateDialog;
